import React, { useEffect } from 'react';
import { useAtom } from 'jotai';
import { useGetCountryList } from '@/features/auth/services/country-list';
import { countriesAtom, findCountryByCode, filterCountries } from '@/features/auth/store/country-storage';
import type { CountryData } from '@/features/auth/store/country-storage';

/**
 * 国家数据管理 Hook
 * 使用 API 获取国家列表并存储在 Jotai 中
 */
export const useCountries = () => {
  const [countries, setCountries] = useAtom(countriesAtom);

  // 优先从 storage 获取数据，只有在 storage 中没有数据时才从接口获取
  const shouldFetch = !countries;

  // 获取国家列表 - 只在没有缓存数据时才发送请求
  const { data: countryListData, isLoading, error } = useGetCountryList.useQueryHook({
    queryKey: ['countries'],
    enabled: shouldFetch,
  });
  // 当 API 数据更新时，同步到 storage
  useEffect(() => {
    if (countryListData?.countries) {
      setCountries(countryListData.countries);
    }
  }, [countryListData, setCountries]);

  // 优先使用 storage 中的数据，如果没有则使用 API 数据
  const effectiveCountries = countries || countryListData?.countries || null;

  // 根据国家代码查找国家
  const findCountry = React.useCallback((code: string): CountryData | undefined => {
    return findCountryByCode(effectiveCountries, code);
  }, [effectiveCountries]);

  // 过滤国家列表
  const getFilteredCountries = React.useCallback((searchValue: string): CountryData[] => {
    return filterCountries(effectiveCountries, searchValue);
  }, [effectiveCountries]);

  return {
    countries: effectiveCountries,
    isLoading,
    error,
    findCountry,
    getFilteredCountries,
  };
};
